import styled, { css } from 'styled-components';
import { Row, Col } from 'components/common/grid';
import { Image } from 'components/common/image';
import { useState, useEffect, useContext, MutableRefObject } from 'react';
import { Icon } from 'components/common/icon/icon';
import { Button } from 'components/common/buttons/button';
import { TextInput } from 'components/common/text-inputs/text-inputs';
import Tooltip from 'components/common/tooltip';
import { useMixpanel } from 'hooks';
import { TranslationContext } from 'contexts/translation-context';
import poweredByStripe from 'assets/images/poweredByStripe.svg';
import {
  CardNumberElement,
  CardCvcElement,
  CardExpiryElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { closeModal } from 'components/common/modal/modal-actions';
import { useDispatch, useSelector } from 'react-redux';
import { attachPaymentMethod, createCustomerAndSetupIntent, makePayment } from '../actions';
import { FormikProps } from 'formik';
import { JoinFormType, PackagePrices } from 'types/join';
import { PlanState } from '../reducers/plan';

const Form = styled.form`
  .InputElement {
    &:focus {
      border-color: ${props => props.theme.colors.siteBlue} !important;
      box-shadow: 0 0 0 2px ${props => props.theme.colors.focusShadowSiteBlue} !important;
    }
  }
`;

const Field = styled.div<{ focused?: boolean }>`
  background: ${props => props.theme.colors.inputBackgroundGrey};
  border: 1px solid ${props => props.theme.colors.inputBorderGrey};
  padding: 10px 11px;
  width: 100%;
  max-width: 100%;
  border-radius: 3px;
  height: 35px;
  transition: all 0.3s ease-in-out;
  ${props => {
    if (props.focused) {
      return css`
        border-color: ${props.theme.colors.siteBlue};
        box-shadow: 0 0 0 2px ${props.theme.colors.focusShadowSiteBlue};
      `;
    }
  }}
`;

const Container = styled(Row)`
  margin-top: 4px;
`;
const StyledCol = styled(Col)`
  text-align: center;
`;

const Title = styled.div`
  width: 442px;
  margin: 8px auto 11px;
  font-size: 3rem;
  text-align: center;
  font-family: ${props => props.theme.fonts.montserratRegular};
`;
const ButtonRow = styled.div`
  position: absolute;
  bottom: 38px;
  left: 50%;
  transform: translateX(-50%);
`;
const CancelButton = styled(Button)`
  margin-right: 8px;
  width: 80px;
`;

const ContinueWithPaymentButton = styled(Button)`
  width: 188px;
`;

const FormCol = styled(Col)`
  width: 423px;
  padding-left: 20px;
`;

const StyledRow = styled(Row)`
  margin: 30px auto 0;
  @media (max-height: 754px) {
    margin: 0px auto;
  }
`;

const Label = styled.label`
  font-size: 1rem;
  margin-bottom: 6px;
  font-family: ${props => props.theme.fonts.montserratSemiBold};
  color: #505050;
`;

const FieldWrapper = styled(Col)`
  margin-bottom: 22px;
  position: relative;
`;

const StyledTextInput = styled(TextInput)`
  font-family: sans-serif;
  color: black;
  ::placeholder {
    color: #505050;
  }
`;

const ErrorField = styled.div`
  color: red;
  font-size: 1.1rem;
  position: absolute;
  top: 58px;
`;

const TooltipIcon = styled(Icon)`
  color: ${props => props.theme.colors.siteBlue};
  font-size: 1.3rem;
`;

const InfoTooltip = styled(Tooltip)`
  &.trigger {
    position: absolute;
    top: 3px;
    left: 31px;
  }
`;

const ProtectedRow = styled(Row)`
  position: absolute;
  bottom: 100px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 1.2rem;
  font-family: ${props => props.theme.fonts.montserratRegular};
`;

const ProtectedIcon = styled(Icon).attrs({
  name: 'lock'
})`
  font-size: 1.8rem;
  margin-right: 7px;
  position: relative;
  top: -2px;
`;

type PaymentFormProps = {
  openedModal: boolean;
  runRef: MutableRefObject<number>;
  isPriceList: boolean;
  form: FormikProps<JoinFormType>;
  selectedPlan?: PackagePrices;
};

export const PaymentForm = ({
  openedModal,
  runRef,
  isPriceList,
  form,
  selectedPlan
}: PaymentFormProps) => {
  const [stripeReady, setStripeReady] = useState(false);
  const { logToMixpanel } = useMixpanel();
  const TRANSLATION_TEXT = useContext(TranslationContext) as { [key: string]: string };
  const [name, setName] = useState();
  const [cardError, setCardError] = useState<string | undefined>();
  const [nameError, setNameError] = useState<string | undefined>();
  const [cardComplete, setCardComplete] = useState();
  const [expComplete, setExpComplete] = useState();
  const [cvvComplete, setCvvComplete] = useState();
  const [expError, setExpError] = useState<string | undefined>();
  const [cvvError, setCvvError] = useState<string | undefined>();
  const [cardFocused, setCardFocused] = useState<boolean | undefined>();
  const [expFocused, setExpFocused] = useState<boolean | undefined>();
  const [cvvFocused, setCvvFocused] = useState<boolean | undefined>();

  const { payment } = useSelector((state: { plan: PlanState }) => state.plan) || {};

  const dispatch = useDispatch();

  const options = {
    showIcon: true,
    style: {
      base: {
        fontSize: '11px',
        color: 'black',
        fontFamily: 'sans-serif',
        '::placeholder': {
          color: '#505050',
          fontFamily: 'sans-serif'
        }
      },
      invalid: {
        color: 'black'
      }
    }
  };
  const stripe = useStripe();
  const elements = useElements();

  useEffect(() => {
    if (stripe && elements) {
      setStripeReady(true);
      if (selectedPlan) {
        dispatch(createCustomerAndSetupIntent(form.values.email || '', selectedPlan.packageName));
      }
    }
  }, [stripe, elements]);

  useEffect(() => {
    if (!openedModal && runRef.current > 0) {
      dispatch(closeModal());
    }
  }, [openedModal]);

  const handleSubmit = async (event: any) => {
    event.preventDefault();
    setCardError('');
    setCvvError('');
    setNameError('');
    setExpError('');

    if (!stripe || !elements || payment === null) {
      return;
    }

    const cardElement = elements.getElement(CardNumberElement);

    const payload = await stripe.createPaymentMethod({
      type: 'card',
      card: cardElement!,
      billing_details: {
        name
      }
    });

    if (payload.error) {
      setCardError(payload.error.message);
      return;
    }
    if (isPriceList) {
      dispatch(
        makePayment(form.values.email || '', payload.paymentMethod.id, payment.customerId || '')
      );
    } else {
      dispatch(
        attachPaymentMethod(
          form.values.email || '',
          payload.paymentMethod.id,
          payment.customerId || ''
        )
      );
    }
  };

  const validate = (field: string, e: any) => {
    if (e.isName) {
      setNameError(e.value ? '' : 'Full Name is required');
    } else if (field === 'number') {
      setCardComplete(e.complete);
      setCardError(e.error ? e.error.message : '');
    } else if (field === 'cvc') {
      setCvvComplete(e.complete);
      setCvvError(e.error ? e.error.message : '');
    } else if (field === 'exp') {
      setExpComplete(e.complete);
      setExpError(e.error ? e.error.message : '');
    }
  };

  return (
    <Container justifyCenter>
      <Col xs={100}>
        <Title>{TRANSLATION_TEXT.make_payment}</Title>
      </Col>
      <StyledCol xs={100}>
        <Image src={poweredByStripe} width="119px" height="26px" />
      </StyledCol>
      <Col xs={100}>
        <StyledRow justifyCenter alignCenter>
          <FormCol>
            <Form onSubmit={handleSubmit} noValidate>
              <Row justifyBetween>
                <FieldWrapper xs={100}>
                  <Label htmlFor="name">Full Name</Label>
                  <StyledTextInput
                    id="name"
                    placeholder="Type here..."
                    value={name}
                    onChange={(e: any) => {
                      setName(e);
                      validate('name', { isName: true, value: e });
                    }}
                  />
                  {nameError && <ErrorField>{nameError}</ErrorField>}
                </FieldWrapper>
                <FieldWrapper xs={50}>
                  <Label htmlFor="cardNumber">Card Number</Label>
                  <Field focused={cardFocused}>
                    {stripeReady && (
                      <CardNumberElement
                        onFocus={() => setCardFocused(true)}
                        onBlur={() => setCardFocused(false)}
                        onChange={e => validate('number', e)}
                        id="cardNumber"
                        options={{ ...options, placeholder: 'Type here...' }}
                      />
                    )}
                  </Field>
                  <ErrorField>
                    {cardError && <div>{cardError}</div>}
                    {cvvError && <div>{cvvError}</div>}
                    {expError && <div>{expError}</div>}
                  </ErrorField>
                </FieldWrapper>
                <FieldWrapper xs={25}>
                  <Label htmlFor="expiry">Expiry Date</Label>
                  <Field focused={expFocused}>
                    {stripeReady && (
                      <CardExpiryElement
                        onFocus={() => setExpFocused(true)}
                        onBlur={() => setExpFocused(false)}
                        onChange={e => validate('exp', e)}
                        id="expiry"
                        options={{ ...options, placeholder: 'MM / YY' }}
                      />
                    )}
                  </Field>
                </FieldWrapper>
                <FieldWrapper xs={25}>
                  <Label htmlFor="cvc">
                    CVV
                    <InfoTooltip tooltip={TRANSLATION_TEXT.cvv_is_the_last_three_digits}>
                      <TooltipIcon name="info" />
                    </InfoTooltip>
                  </Label>
                  <Field focused={cvvFocused}>
                    {stripeReady && (
                      <CardCvcElement
                        onFocus={() => setCvvFocused(true)}
                        onBlur={() => setCvvFocused(false)}
                        onChange={e => validate('cvc', e)}
                        id="cvc"
                        options={{ ...options, placeholder: 'Type here...' }}
                      />
                    )}
                  </Field>
                </FieldWrapper>
              </Row>
              <ProtectedRow>
                <ProtectedIcon />
                {TRANSLATION_TEXT.secured_transaction}
              </ProtectedRow>
              <ButtonRow>
                <CancelButton onClick={() => dispatch(closeModal())}>
                  {TRANSLATION_TEXT.cancel}
                </CancelButton>
                <ContinueWithPaymentButton
                  blue
                  ids={['MAKE_PAYMENT']}
                  disabled={
                    !stripe ||
                    !elements ||
                    !!nameError ||
                    !cardComplete ||
                    !cvvComplete ||
                    !expComplete ||
                    !name
                  }
                  submit
                >
                  {TRANSLATION_TEXT.continue_with_payment}
                </ContinueWithPaymentButton>
              </ButtonRow>
            </Form>
          </FormCol>
        </StyledRow>
      </Col>
    </Container>
  );
};
